'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  EMPTY_STARTUP_PROGRESS,
  readStartupProgress,
  readStartupProgressHttp,
  type StartupProgress,
} from '@/lib/desktop/bridge'
import { isDesktopBridgeAvailable } from '@/lib/desktop/bridge'
import { runtimeTarget } from '@/lib/runtime-target'
import { Progress } from '@/components/ui/progress'

const POLL_INTERVAL_MS = 600
const READY_CHECK_TIMEOUT_MS = 3000
// If we can't read any progress payload after this long (e.g. Tauri bridge
// unavailable / progress file never written), fall through to the API probe
// so the user isn't stuck on the boot screen forever.
const READY_FALLBACK_MS = 30000

// Animation speed for the progress bar. The display value creeps toward the
// real (server) percentage at this rate, which keeps the bar moving smoothly
// between step updates and animates toward each new target.
const PROGRESS_SPEED_PCT_PER_SEC = 10

/**
 * Smoothly animated, monotonic progress value.
 *
 * - The returned value only ever increases — a target smaller than the current
 *   display value is ignored (no backward jumps).
 * - When a larger target arrives, the value animates toward it at a constant
 *   speed, pausing once it catches up until the next larger target.
 * - Driven by requestAnimationFrame so the bar moves smoothly between the
 *   discrete step percentages reported by the poller.
 */
function useSmoothProgress(target: number): number {
  const [display, setDisplay] = useState(0)
  const displayRef = useRef(0)
  const targetRef = useRef(0)
  const runningRef = useRef(false)

  const start = useCallback(() => {
    if (runningRef.current) return
    runningRef.current = true
    let last = performance.now()

    const tick = (now: number) => {
      // Clamp dt so a long frame pause (e.g. tab switch) doesn't overshoot wildly.
      const dt = Math.min((now - last) / 1000, 0.1)
      last = now

      const next = Math.min(
        targetRef.current,
        displayRef.current + PROGRESS_SPEED_PCT_PER_SEC * dt,
      )
      displayRef.current = next
      setDisplay(next)

      if (displayRef.current < targetRef.current) {
        requestAnimationFrame(tick)
      } else {
        runningRef.current = false
      }
    }

    requestAnimationFrame(tick)
  }, [])

  useEffect(() => {
    // Accept only forward progress; smaller/equal targets are ignored.
    if (target > targetRef.current) {
      targetRef.current = target
      start()
    }
  }, [target, start])

  return display
}

/** Resolve the local API base URL the same way @/lib/config does. */
function resolveApiBaseUrl(): string {
  const envDesktop = process.env.NEXT_PUBLIC_DESKTOP_API_URL
  const envApi = process.env.NEXT_PUBLIC_API_URL
  const fallback = runtimeTarget === 'desktop'
    ? 'http://127.0.0.1:50505'
    : 'http://localhost:5055'
  if (runtimeTarget === 'desktop') {
    return envDesktop || envApi || fallback
  }
  return envApi || fallback
}

/**
 * Non-blocking "is the API usable yet?" probe.
 *
 * Deliberately avoids getConfig()/fetchConfig() — those loop up to 60×1s
 * retrying while the DB is coming up, which would freeze the progress bar.
 */
async function isApiReady(): Promise<boolean> {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), READY_CHECK_TIMEOUT_MS)
    try {
      const resp = await fetch(`${resolveApiBaseUrl()}/agent-api/config`, {
        cache: 'no-store',
        signal: controller.signal,
      })
      if (!resp.ok) return false
      const data = await resp.json()
      return data?.dbStatus === 'online'
    } finally {
      clearTimeout(timer)
    }
  } catch {
    return false
  }
}

export function StartupProgress({ onReady }: { onReady?: () => void }) {
  const [progress, setProgress] = useState<StartupProgress>(EMPTY_STARTUP_PROGRESS)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const tauriAvailable = isDesktopBridgeAvailable()

  // Once the API is reachable and the boot finished, notify the parent
  // ConnectionGuard so it swaps in the main UI.
  useEffect(() => {
    if (ready) {
      onReady?.()
    }
  }, [ready, onReady])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const mountedAt = Date.now()

    const poll = async () => {
      if (cancelled) return

      // Try the Tauri file channel first (works before API is up); in web
      // mode or as a fallback, poll the HTTP endpoint.
      let data = EMPTY_STARTUP_PROGRESS
      if (tauriAvailable) {
        data = await readStartupProgress()
        // If the Tauri file channel returns nothing (e.g. progress file not
        // yet written), fall back to the HTTP endpoint in case the API is
        // already up.
        if (!data.steps?.length) {
          const http = await readStartupProgressHttp()
          if (http.steps?.length) data = http
        }
      } else {
        data = await readStartupProgressHttp()
      }

      if (cancelled) return

      if (data.steps?.length) {
        setProgress(data)
        setError(data.error ?? null)
      }

      // Enter the app only when the progress payload says the boot finished
      // (all steps done) AND the API is reachable. Without a progress payload
      // at all (e.g. no Tauri bridge, no file), fall back to the API probe
      // after a generous timeout so the user isn't stuck forever.
      const elapsed = Date.now() - mountedAt
      const shouldCheckReady = data.finished || (elapsed > READY_FALLBACK_MS)
      if (shouldCheckReady && !ready) {
        if (await isApiReady()) {
          setReady(true)
          return
        }
      }

      timer = setTimeout(poll, POLL_INTERVAL_MS)
    }

    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [tauriAvailable, ready])

  const steps = progress.steps?.length
    ? progress.steps
    : []
  const total = steps.length
  const doneCount = steps.filter(s => s.status === 'done' || s.status === 'failed').length
  const targetPercent = total > 0 ? Math.round((doneCount / total) * 100) : 0

  // NOTE: hooks must be called unconditionally on EVERY render, before any
  // early return. Computing the animated value here (before the `ready` guard)
  // ensures the hook count stays constant across re-renders — otherwise React
  // throws "Rendered fewer hooks than expected".
  const displayPercent = useSmoothProgress(targetPercent)

  // If the API is already up (e.g. second mount after error retry), bail out.
  if (ready) {
    return null
  }

  const currentLabel = steps.find(s => s.status === 'running')?.label ?? null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background">
      <div className="flex w-full max-w-md flex-col items-center gap-6 px-6 text-center">
        <div className="flex flex-col items-center gap-2">
          <div className="text-lg font-semibold">即策AI</div>
          <div className="text-sm text-muted-foreground">
            {currentLabel
              ? `正在${currentLabel}...`
              : runtimeTarget === 'desktop'
                ? '正在启动...'
                : '正在连接服务...'}
          </div>
        </div>

        {error && (
          <div className="w-full rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="w-full space-y-1">
          <Progress
            value={displayPercent}
            // The rAF-driven animation provides the smoothing; disable the
            // CSS transition so the indicator tracks each frame directly.
            indicatorClassName="transition-none"
          />
          <div className="flex justify-center text-xs text-muted-foreground">
            <span>{Math.round(displayPercent)}%</span>
          </div>
        </div>
      </div>
    </div>
  )
}
